import { FC, useContext, useEffect, useState } from 'react';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressIndicator from '@components/ProgressIndicator';
import { useUser } from '@hooks';
import userWorkflowToContext from '@helpers/userWorkflowToContext';
import MainPanelSubLayout from '@/layouts/MainPanelSubLayout';
import { DocumentAccessContext, DocumentAccessGate } from '@/providers/DocumentAccess';
import { CommonUserWorkflowCompiledDocument } from '@/tsClient/servers/noodleApi/ApiDefinition/workflows';
import { Document } from '@/typings/api-models';
import Content from '@/components/Document/DocumentPreview/Content';
import preloadDocument from '@/helpers/preloadDocument';
import { logError } from '@/providers/ErrorTracking';
import PacerDocumentRow from '@/components/PacerDocumentRow';
import ECFManagerModal from '@/modals/ECFManagerModal';
import { getUrl, IDENTIFIERS } from '@/helpers/urlsHelper';
import { useRouter } from 'next/router';
import Button from '@/components/DesignLibrary/Button';
import s from './PacerPreviewPane.module.scss';

type Props = {
  userWorkflowId: string;
};

async function blobToBase64(blob: Blob): Promise<string> {
  const retVal = await new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
  return retVal as string;
}

const PacerPreviewPane: FC<Props> = ({ userWorkflowId }) => {
  const router = useRouter();
  const { accessToken } = useContext(DocumentAccessContext);
  const { data: documents, getData: getUserWorkflowDocuments } = useNoodleApi(tsClient.workflows.getUserWorkflowCompiledDocuments);
  const [user] = useUser();
  const [selectedDocument, setSelectedDocument] = useState<Pick<Document, 'id' | 'createdAt' | 'fileName' | 'mimeType' | 'creatorId' | 'personId' | 'isCollaborator'> | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [_base64Blob, setBase64Blob] = useState<string | null>(null);
  const [ecfCaseModalOpen, setEcfCaseModalOpen] = useState(false);
  const [pacerDocs, setPacerDocs] = useState<CommonUserWorkflowCompiledDocument[]>([]);

  const handleRouteToUserWorkflowDetail = async (uwId: string): Promise<void> => {
    router.push(getUrl(IDENTIFIERS.DASHBOARD_USER_WORKFLOW_DETAIL, { userWorkflowId: uwId }));
  };

  const {
    data: userWorkflow,
    fetchingState: userWorkflowFetchingState,
    getData: getUserWorkflowFn,
  } = useNoodleApi(tsClient.workflows.getUserWorkflowById);

  useEffect(() => {
    getUserWorkflowDocuments({
      page: 1,
      perPage: 100_000,
      userWorkflowId,
    });
    getUserWorkflowFn({
      userWorkflowId,
    });
  }, [userWorkflowId, getUserWorkflowDocuments, getUserWorkflowFn]);

  useEffect(() => {
    if (documents && documents.items) {
      const pacerDocuments = documents.items.filter(doc => doc.integrationSlug === 'pacer');
      setPacerDocs(pacerDocuments);
    }
  }, [documents]);

  useEffect(() => {
    if (pacerDocs && pacerDocs.length > 0 && pacerDocs[0].document) {
      setSelectedDocument(pacerDocs[0].document);
    }
  }, [pacerDocs]);

  useEffect(() => {
    if ((accessToken || user?.id) && selectedDocument?.id) {
      preloadDocument({
        accessToken,
        id: selectedDocument.id,
      })
        .then(blob => {
          const typeBlob = new Blob([blob], { type: selectedDocument.mimeType });
          const blobPreviewUrl = window.URL.createObjectURL(typeBlob);
          blobToBase64(blob).then(res => {
            // The API doesn't take `application/octet-stream`
            const remimedBase64Blob = res.replace('application/octet-stream', selectedDocument.mimeType);

            setBase64Blob(remimedBase64Blob);
          });

          setPreviewUrl(blobPreviewUrl);
        })
        .catch(error => {
          logError(error);
        });
    }
  }, [selectedDocument, user?.id, selectedDocument?.mimeType, accessToken]);

  if (!documents?.items) {
    return (
      <ProgressIndicator isCentered isPage />
    );
  }

  return (
    <>
      <MainPanelSubLayout
        homeTabTitle='PACER preview'
        header={{
          description: 'Please review before syncing the case data. Glade Ai will only file the required documents for your district.',
          title: 'Forms ready for filing',
        }}
        pageContext={{ isFetching: userWorkflowFetchingState.isFetching, items: userWorkflowToContext(userWorkflow) }}
      >
        {ecfCaseModalOpen
          && <ECFManagerModal
            documents={pacerDocs}
            onClose={() => setEcfCaseModalOpen(false)}
            onSubmit={() => handleRouteToUserWorkflowDetail(userWorkflowId)}
            userWorkflowId={userWorkflowId}
          />}
        <div className={s.splitPane}>
          <div className={s.pacerList}>
            <div className={s.pacerScrollContent}>
              {pacerDocs.map((doc) =>
                <PacerDocumentRow
                  compiledDocument={doc}
                  key={doc.id}
                  onClick={() => setSelectedDocument(doc.document)}
                />,
              )}
            </div>
            <Button
              className={s.submitButton}
              size="lg"
              variant="ai"
              onClick={() => setEcfCaseModalOpen(true)}
              fullWidth
            >
              Sync Case Data to PACER
            </Button>
          </div>
          <div className={s.previewPane}>
            {selectedDocument && previewUrl && (
              <DocumentAccessGate
                documentCreatorId={selectedDocument.creatorId}
                documentPersonId={selectedDocument.personId}
                isCollaborator={selectedDocument.isCollaborator}
              >
                <Content url={previewUrl} mimeType={selectedDocument.mimeType} />
              </DocumentAccessGate>
            )}
          </div>
        </div>
      </MainPanelSubLayout>
    </>
  );
};

export default PacerPreviewPane;
